import React, { CSSProperties, ReactNode, useMemo, useCallback } from 'react';
import { useTimelineHeadersContext } from './HeadersContext';
import { useTimelineState } from '../timeline/TimelineStateContext';
import { iterateTimes } from '../utility/calendar';
import type { Interval, TimelineTimeSteps } from '../types/main';
import type { Dayjs } from 'dayjs';
import type { CustomDateHeaderProps } from './CustomDateHeader';
import type { GetIntervalPropsType } from './types';

export interface CustomHeaderProps<Data> {
  children: (p: CustomDateHeaderProps<Data>) => ReactNode;
  unit: keyof TimelineTimeSteps;
  timeSteps: TimelineTimeSteps;
  visibleTimeStart: number;
  visibleTimeEnd: number;
  canvasTimeStart: number;
  canvasTimeEnd: number;
  canvasWidth: number;
  showPeriod: (start: Dayjs, end: Dayjs) => void;
  headerData?: Data;
  getLeftOffsetFromDate: (date: number) => number;
  height: number;
  timelineWidth: number;
}

interface GetHeaderIntervalsParams {
  canvasTimeStart: number;
  canvasTimeEnd: number;
  unit: keyof TimelineTimeSteps;
  timeSteps: TimelineTimeSteps;
  getLeftOffsetFromDate: (date: number) => number;
}

function getHeaderIntervals({
  canvasTimeStart,
  canvasTimeEnd,
  unit,
  timeSteps,
  getLeftOffsetFromDate,
}: GetHeaderIntervalsParams): Interval[] {
  const intervals: Interval[] = [];
  iterateTimes(canvasTimeStart, canvasTimeEnd, unit, timeSteps, (startTime, endTime) => {
    const left = getLeftOffsetFromDate(startTime.valueOf());
    const right = getLeftOffsetFromDate(endTime.valueOf());
    intervals.push({
      startTime,
      endTime,
      labelWidth: right - left,
      left,
    });
  });
  return intervals;
}

function CustomHeaderInner<Data>({
  children: Renderer,
  unit,
  timeSteps,
  visibleTimeStart,
  visibleTimeEnd,
  canvasTimeStart,
  canvasTimeEnd,
  canvasWidth,
  showPeriod,
  headerData,
  getLeftOffsetFromDate,
  height = 30,
  timelineWidth,
}: CustomHeaderProps<Data>) {
  const intervals = useMemo(
    () =>
      getHeaderIntervals({
        canvasTimeStart,
        canvasTimeEnd,
        unit,
        timeSteps,
        getLeftOffsetFromDate,
      }),
    [canvasTimeStart, canvasTimeEnd, unit, timeSteps, getLeftOffsetFromDate]
  );

  const getRootProps = useCallback(
    (props: { style?: CSSProperties } = {}) => ({
      style: {
        ...(props.style ?? {}),
        position: 'relative' as const,
        width: canvasWidth,
        height,
      },
    }),
    [canvasWidth, height]
  );

  const getIntervalProps: GetIntervalPropsType = useCallback(
    (props?: { interval?: Interval; style?: CSSProperties }) => {
      const { interval, style } = props ?? {};
      if (!interval) throw new Error('you should provide interval to the prop getter');
      const { startTime, labelWidth, left } = interval;
      return {
        style: {
          ...style,
          left,
          width: labelWidth,
          position: 'absolute',
        },
        key: `label-${startTime.valueOf()}`,
      };
    },
    []
  );

  const headerProps = useMemo(
    () => ({
      headerContext: { unit, intervals },
      getRootProps,
      getIntervalProps,
      showPeriod,
      data: headerData as CustomDateHeaderProps<Data>['data'],
    }),
    [unit, intervals, getRootProps, getIntervalProps, showPeriod, headerData]
  );

  return <Renderer {...headerProps} />;
}

export type CustomHeaderWrapperProps<Data> = {
  children: (p: CustomDateHeaderProps<Data>) => ReactNode;
  unit?: keyof TimelineTimeSteps;
  headerData?: Data;
  height?: number;
};

function CustomHeaderWrapper<Data>({
  children,
  unit,
  headerData,
  height,
}: CustomHeaderWrapperProps<Data>) {
  const { getTimelineState, showPeriod, getLeftOffsetFromDate } = useTimelineState();
  const timelineState = getTimelineState();
  const { timeSteps } = useTimelineHeadersContext();

  return (
    <CustomHeaderInner
      children={children}
      timeSteps={timeSteps as unknown as TimelineTimeSteps}
      showPeriod={showPeriod}
      unit={unit ?? timelineState.timelineUnit}
      visibleTimeStart={timelineState.visibleTimeStart}
      visibleTimeEnd={timelineState.visibleTimeEnd}
      canvasTimeStart={timelineState.canvasTimeStart}
      canvasTimeEnd={timelineState.canvasTimeEnd}
      canvasWidth={timelineState.canvasWidth}
      timelineWidth={timelineState.timelineWidth}
      headerData={headerData}
      getLeftOffsetFromDate={getLeftOffsetFromDate}
      height={height ?? 30}
    />
  );
}

CustomHeaderWrapper.displayName = 'CustomHeaderWrapper';

export default CustomHeaderWrapper;
